import { apiClient, extractArrayFromResponse, extractObjectFromResponse, ApiResponse } from '../utils/apiClient';
import { cachedApiRequest } from '../utils/apiCache';
// import { logger } from "../utils/logger";
import { Advertisement, AdvertisementType, AdvertisementPlacement, CreateAdvertisementDto, UpdateAdvertisementDto, AdvertisementSearchParams, AdRequestParams, AdResponse, AdvertisementPerformanceParams, AdvertisementAnalytics } from '../../shared/types/advertising';
import { safeGetItem, safeSetItem } from '../utils/storageUtils';
import { logger } from "../utils/logger";
/**
 * Advertisement Service - Handles all advertisement-related operations
 * Provides caching, performance optimization, and consistent error handling
 */
class AdvertisementService {
    private readonly lastCallTime: Record<string, number> = {};
    private readonly adCache: Record<string, {
        ads: Advertisement[];
        timestamp: number;
    }> = {};
    private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
    private readonly RATE_LIMIT_DURATION = 1000; // 1 second
    private readonly REQUEST_TIMEOUT = 5000; // 5 seconds for ad requests
    private readonly CACHE_KEY_PREFIX = 'ad_cache_';
    private readonly SESSION_KEY = 'ad_session_id';
    // ========================================
    // UTILITY METHODS
    // ========================================
    /**
     * Generates a unique session ID for advertisement tracking
     * @returns A unique session identifier
     */
    public generateSessionId(): string {
        return `ad_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    }
    /**
     * Gets the current page URL for advertisement context
     * @returns The current page URL
     */
    public getCurrentPageUrl(): string {
        return window.location.href;
    }
    /**
     * Gets the user agent string for device detection
     * @returns The user agent string
     */
    public getUserAgent(): string {
        return navigator.userAgent;
    }
    /**
     * Detects the device type based on user agent
     * @returns Device type: 'mobile', 'tablet', or 'desktop'
     */
    public detectDevice(): 'mobile' | 'tablet' | 'desktop' {
        const userAgent = navigator.userAgent.toLowerCase();
        if (/mobile|android|iphone|phone/i.test(userAgent)) {
            return 'mobile';
        }
        else if (/tablet|ipad/i.test(userAgent)) {
            return 'tablet';
        }
        else {
            return 'desktop';
        }
    }
    /**
     * Gets or creates a persistent session ID for advertisement tracking
     * @returns The session ID
     */
    private getSessionId(): string {
        let sessionId = safeGetItem(this.SESSION_KEY);
        if (!sessionId) {
            sessionId = this.generateSessionId();
            safeSetItem(this.SESSION_KEY, sessionId);
        }
        return sessionId;
    }
    // ========================================
    // ADVERTISEMENT OPERATIONS
    // ========================================
    /**
     * Fetches advertisements with optional filtering and pagination
     * @param filters - Search and filter parameters
     * @returns Promise resolving to array of advertisements
     */
    async getAdvertisements(filters: AdvertisementSearchParams = {}): Promise<Advertisement[]> {
        try {
            const params = new URLSearchParams();
            if (filters.page)
                params.append('page', filters.page.toString());
            if (filters.limit)
                params.append('limit', filters.limit.toString());
            if (filters.search)
                params.append('search', filters.search);
            if (filters.ad_type_id)
                params.append('ad_type_id', filters.ad_type_id.toString());
            if (filters.placement_id)
                params.append('placement_id', filters.placement_id.toString());
            if (filters.content_type)
                params.append('content_type', filters.content_type);
            if (filters.is_active !== undefined)
                params.append('is_active', filters.is_active.toString());
            if (filters.start_date)
                params.append('start_date', filters.start_date);
            if (filters.end_date)
                params.append('end_date', filters.end_date);
            if (filters.sortBy)
                params.append('sortBy', filters.sortBy);
            if (filters.sortOrder)
                params.append('sortOrder', filters.sortOrder);
            const response = await apiClient.request<Advertisement[]>(`/advertisements?${params.toString()}`, {
                requiresAuth: true,
                timeout: 10000
            });
            return extractArrayFromResponse<Advertisement>(response, []);
        }
        catch (error) {
            logger.error('Error fetching advertisements:', error, {});
            throw new Error(`Failed to fetch advertisements: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }
    /**
     * Fetches available advertisement types
     * @returns Promise resolving to advertisement types with success status
     */
    async getAdvertisementTypes(): Promise<{
        success: boolean;
        data: AdvertisementType[];
    }> {
        try {
            const response = await apiClient.request<AdvertisementType[]>('/advertisements/types', {
                requiresAuth: true,
                timeout: 10000
            });
            const data = extractArrayFromResponse<AdvertisementType>(response, []);
            return { success: true, data };
        }
        catch (error) {
            logger.error('Error fetching advertisement types:', error, {});
            return { success: false, data: [] };
        }
    }
    /**
     * Fetches available advertisement placements
     * @returns Promise resolving to advertisement placements with success status
     */
    async getAdvertisementPlacements(): Promise<{
        success: boolean;
        data: AdvertisementPlacement[];
    }> {
        try {
            const response = await apiClient.request<AdvertisementPlacement[]>('/advertisements/placements', {
                requiresAuth: true,
                timeout: 10000
            });
            const data = extractArrayFromResponse<AdvertisementPlacement>(response, []);
            return { success: true, data };
        }
        catch (error) {
            logger.error('Error fetching advertisement placements:', error, {});
            return { success: false, data: [] };
        }
    }
    /**
     * Creates a new advertisement
     * @param adData - Advertisement creation data
     * @returns Promise resolving to the created advertisement
     */
    async createAdvertisement(adData: CreateAdvertisementDto): Promise<Advertisement> {
        try {
            const response = await apiClient.request<Advertisement>('/advertisements', {
                method: 'POST',
                body: adData,
                requiresAuth: true,
                timeout: 15000
            });
            return extractObjectFromResponse<Advertisement>(response, {} as Advertisement);
        }
        catch (error) {
            logger.error('Error creating advertisement:', error, {});
            throw new Error(`Failed to create advertisement: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }
    /**
     * Updates an existing advertisement
     * @param id - Advertisement ID to update
     * @param adData - Advertisement update data
     * @returns Promise resolving to the updated advertisement
     */
    async updateAdvertisement(id: number, adData: UpdateAdvertisementDto): Promise<Advertisement> {
        try {
            const response = await apiClient.request<Advertisement>(`/advertisements/${id}`, {
                method: 'PUT',
                body: adData,
                requiresAuth: true,
                timeout: 15000
            });
            return extractObjectFromResponse<Advertisement>(response, {} as Advertisement);
        }
        catch (error) {
            logger.error('Error updating advertisement:', error, {});
            throw new Error(`Failed to update advertisement: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }
    /**
     * Deletes an advertisement
     * @param id - Advertisement ID to delete
     * @returns Promise that resolves when deletion is complete
     */
    async deleteAdvertisement(id: number): Promise<void> {
        try {
            await apiClient.request(`/advertisements/${id}`, {
                method: 'DELETE',
                requiresAuth: true,
                timeout: 10000
            });
        }
        catch (error) {
            logger.error('Error deleting advertisement:', error, {});
            throw new Error(`Failed to delete advertisement: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }
    // ========================================
    // ADVERTISEMENT SERVING
    // ========================================
    /**
     * Generates a cache key for advertisement requests
     * @param params - Request parameters
     * @returns Cache key string
     */
    private generateCacheKey(params: AdRequestParams): string {
        return `${this.CACHE_KEY_PREFIX}${params.placement}_${params.user_type || 'free'}_${params.device || 'desktop'}_${params.user_id || 'anonymous'}`;
    }
    /**
     * Serves advertisements based on request parameters with caching and rate limiting
     * @param params - Advertisement request parameters
     * @returns Promise resolving to advertisement response
     */
    async serveAdvertisements(params: AdRequestParams): Promise<AdResponse> {
        const now = Date.now();
        const cacheKey = this.generateCacheKey(params);
        // Check cache first
        if (this.adCache[cacheKey] && (now - this.adCache[cacheKey].timestamp) < this.CACHE_DURATION) {
            return { success: true, ads: this.adCache[cacheKey].ads };
        }
        // Rate limiting check
        if (this.lastCallTime[cacheKey] && (now - this.lastCallTime[cacheKey]) < this.RATE_LIMIT_DURATION) {
            // Return cached result if available, otherwise empty array
            if (this.adCache[cacheKey]) {
                return { success: true, ads: this.adCache[cacheKey].ads };
            }
            return { success: true, ads: [] };
        }
        this.lastCallTime[cacheKey] = now;
        try {
            const queryParams = new URLSearchParams();
            queryParams.append('placement', params.placement);
            if (params.user_id)
                queryParams.append('user_id', params.user_id.toString());
            // Don't include session_id in cache key to allow proper caching
            if (params.session_id)
                queryParams.append('session_id', params.session_id);
            if (params.page_url)
                queryParams.append('page_url', params.page_url);
            if (params.user_agent)
                queryParams.append('user_agent', params.user_agent);
            if (params.user_type)
                queryParams.append('user_type', params.user_type);
            if (params.location)
                queryParams.append('location', params.location);
            if (params.device)
                queryParams.append('device', params.device);
            if (params.page_type)
                queryParams.append('page_type', params.page_type);
            if (params.category)
                queryParams.append('category', params.category);
            if (params.keyword)
                queryParams.append('keyword', params.keyword);
            if (params.ip_address)
                queryParams.append('ip_address', params.ip_address);
            if (params.referrer)
                queryParams.append('referrer', params.referrer);
            // Use cached API request to prevent rate limiting
            const response = await cachedApiRequest<{ success?: boolean; ads?: Advertisement[]; data?: Advertisement[] }>(`/api/advertisements/serve?${queryParams.toString()}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            }, 2 * 60 * 1000, `api:advertisements:${cacheKey}`); // Use custom cache key without session_id
            // /api/advertisements/serve returns { success, ads, settings } — not { data: [...] }
            const ads = Array.isArray(response?.ads)
                ? response.ads
                : Array.isArray(response?.data)
                    ? response.data
                    : [];
            // Cache the successful result
            this.adCache[cacheKey] = {
                ads,
                timestamp: now
            };
            return { success: true, ads };
        }
        catch (error: unknown) {
            // Log error for debugging but don't throw
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            // Handle rate limiting specifically
            if (errorMessage.includes('Too many requests') || errorMessage.includes('429')) {
                logger.warn('Advertisement service rate limited (non-critical):', { "arg0": errorMessage });
                return { success: true, ads: [], error: 'Rate limited - will retry later' };
            }
            logger.error('Advertisement service error (non-critical):', errorMessage, {});
            // Never fail UX because of ads
            return { success: true, ads: [], error: 'Failed to load advertisements' };
        }
    }
    // ========================================
    // ADVERTISEMENT TRACKING
    // ========================================
    /**
     * Tracks an advertisement click event
     * @param adId - Advertisement ID that was clicked
     * @param trackingData - Additional tracking data
     * @returns Promise that resolves when tracking is complete
     */
    async trackAdClick(adId: number, trackingData?: Partial<AdRequestParams>): Promise<void> {
        try {
            const trackingPayload = {
                ad_id: adId,
                session_id: this.getSessionId(),
                page_url: this.getCurrentPageUrl(),
                user_agent: this.getUserAgent(),
                device: this.detectDevice(),
                timestamp: new Date().toISOString(),
                ...trackingData
            };
            // Track in GA4
            if (typeof window !== 'undefined' && window.gtag) {
                const eventId = `ad_click_${adId}_${Date.now()}`;
                window.gtag('event', 'ad_click', {
                    ad_id: adId.toString(),
                    ad_format: trackingData?.ad_format || 'unknown',
                    page_location: this.getCurrentPageUrl(),
                    event_id: eventId, // For deduplication
                });
            }
            // Track in database
            await apiClient.request('/advertisements/track-click', {
                method: 'POST',
                body: trackingPayload,
                requiresAuth: false, // Tracking doesn't require authentication
                timeout: 5000
            });
        }
        catch (error) {
            logger.error('Error tracking ad click:', error, {});
            // Don't throw - tracking failures shouldn't break the UI
        }
    }
    /**
     * Tracks an advertisement impression event
     * @param adId - Advertisement ID that was viewed
     * @param trackingData - Additional tracking data
     * @returns Promise that resolves when tracking is complete
     */
    async trackAdImpression(adId: number, trackingData?: Partial<AdRequestParams>): Promise<void> {
        try {
            const trackingPayload = {
                ad_id: adId,
                session_id: this.getSessionId(),
                page_url: this.getCurrentPageUrl(),
                user_agent: this.getUserAgent(),
                device: this.detectDevice(),
                timestamp: new Date().toISOString(),
                ...trackingData
            };
            // Track in GA4
            if (typeof window !== 'undefined' && window.gtag) {
                const eventId = `ad_impression_${adId}_${Date.now()}`;
                window.gtag('event', 'ad_impression', {
                    ad_id: adId.toString(),
                    ad_format: trackingData?.ad_format || 'unknown',
                    page_location: this.getCurrentPageUrl(),
                    event_id: eventId, // For deduplication
                });
            }
            // Track in database
            await apiClient.request('/advertisements/track-impression', {
                method: 'POST',
                body: trackingPayload,
                requiresAuth: false, // Tracking doesn't require authentication
                timeout: 5000
            });
        }
        catch (error) {
            logger.error('Error tracking ad impression:', error, {});
            // Don't throw - tracking failures shouldn't break the UI
        }
    }
    /**
     * Gets advertisement performance analytics
     * @param params - Performance query parameters
     * @returns Promise resolving to performance analytics
     */
    async getAdvertisementPerformance(params: AdvertisementPerformanceParams = {}): Promise<AdvertisementAnalytics> {
        try {
            const queryParams = new URLSearchParams();
            if (params.ad_id)
                queryParams.append('ad_id', params.ad_id.toString());
            if (params.user_id)
                queryParams.append('user_id', params.user_id.toString());
            if (params.event_type)
                queryParams.append('event_type', params.event_type);
            if (params.start_date)
                queryParams.append('start_date', params.start_date);
            if (params.end_date)
                queryParams.append('end_date', params.end_date);
            if (params.page)
                queryParams.append('page', params.page.toString());
            if (params.limit)
                queryParams.append('limit', params.limit.toString());
            const response = await apiClient.request<AdvertisementAnalytics>(`/advertisements/performance?${queryParams.toString()}`, {
                requiresAuth: true,
                timeout: 10000
            });
            return extractObjectFromResponse<AdvertisementAnalytics>(response, {
                total_impressions: 0,
                total_clicks: 0,
                click_through_rate: 0,
                total_conversions: 0,
                conversion_rate: 0,
                revenue: 0,
                cost_per_click: 0,
                cost_per_impression: 0
            });
        }
        catch (error) {
            logger.error('Error fetching advertisement performance:', error, {});
            throw new Error(`Failed to fetch advertisement performance: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }
    /**
     * Clears the advertisement cache
     * Useful for testing or when cache needs to be refreshed
     */
    clearCache(): void {
        Object.keys(this.adCache).forEach(key => {
            delete this.adCache[key];
        });
        Object.keys(this.lastCallTime).forEach(key => {
            delete this.lastCallTime[key];
        });
    }
    /**
     * Gets cache statistics for debugging
     * @returns Cache statistics object
     */
    getCacheStats(): {
        cacheSize: number;
        rateLimitEntries: number;
    } {
        return {
            cacheSize: Object.keys(this.adCache).length,
            rateLimitEntries: Object.keys(this.lastCallTime).length
        };
    }
    /**
     * Gets keyword advertisements for content processing
     * @returns Promise resolving to keyword ads response
     */
    async getKeywordAds(): Promise<{
        success: boolean;
        keywordAds?: any[];
    }> {
        try {
            // For now, return empty array to prevent errors
            // This can be implemented later when keyword ads are needed
            return {
                success: true,
                keywordAds: []
            };
        }
        catch (error) {
            logger.error('Error fetching keyword ads:', error, {});
            return {
                success: false,
                keywordAds: []
            };
        }
    }
}
// Export singleton instance
export const advertisementService = new AdvertisementService();
export default advertisementService;
